import { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { format, formatDistanceToNow } from 'date-fns'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { api } from '../../services/api'
import { useQuotaStatus } from '../../hooks/useSubscription' // Phase 3 Integration
import {
  getSuggestedTimeSlots,
  filterAvailableTime,
  validateWithCascadeOption
} from '../../utils/scheduleValidation'
import { getDateLocale } from '../../utils/dateLocale'
import { buildBlobUrl } from '../../utils/blobUrl'
import { FALLBACK_IMAGE, applyFallbackIfNeeded } from '../../utils/imageFallback'

const resolveScheduledAt = (story) =>
  story?.scheduled_for || story?.scheduledFor || story?.scheduled_at || story?.scheduledAt

const getStatusColor = (status) => {
  switch (status) {
    case 'scheduled':
      return 'bg-blue-100 text-blue-800'
    case 'uploading':
      return 'bg-yellow-100 text-yellow-800'
    case 'posted':
      return 'bg-green-100 text-green-800'
    case 'failed':
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const resolveImageUrl = (story) => {
  return (
    story?.rendered_sas_url ||
    story?.sas_url ||
    buildBlobUrl(story?.rendered_blob_path) ||
    FALLBACK_IMAGE
  )
}

function UpcomingSchedule({ stories, loading, error, onRefresh, instagramAccountId, draftMode = false, onDraftChange = null }) {
  const { t, i18n } = useTranslation('dashboard')
  const [editingStoryId, setEditingStoryId] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [feedback, setFeedback] = useState(null)
  const [returningStoryId, setReturningStoryId] = useState(null)
  const [regeneratingStoryId, setRegeneratingStoryId] = useState(null)
  const [brandSettings, setBrandSettings] = useState(null)
  const [expandedActions, setExpandedActions] = useState(null)

  // Cascading reschedule state
  const [showCascadeConfirm, setShowCascadeConfirm] = useState(false)
  const [cascadeInfo, setCascadeInfo] = useState(null)
  const [pendingStoryId, setPendingStoryId] = useState(null)

  // Phase 3: Check regeneration quota at account-level
  const regenQuota = useQuotaStatus('regenerations', instagramAccountId)

  // Load brand settings to get post_interval_hours
  useEffect(() => {
    const loadBrandSettings = async () => {
      if (!instagramAccountId) return

      try {
        const settings = await api.getBrandSettings(instagramAccountId)
        setBrandSettings(settings)
      } catch (err) {
        console.error('Failed to load brand settings:', err)
        // Use default
        setBrandSettings({ post_interval_hours: 24 })
      }
    }

    loadBrandSettings()
  }, [instagramAccountId])

  const sortedStories = useMemo(() => {
    if (!stories || stories.length === 0) {
      return []
    }

    return stories
      .slice()
      .filter((story) => Boolean(resolveScheduledAt(story)))
      .sort((a, b) => new Date(resolveScheduledAt(a)) - new Date(resolveScheduledAt(b)))
  }, [stories])

  const nextStory = sortedStories[0]

  const beginEditing = (story) => {
    const scheduledFor = resolveScheduledAt(story)
    setEditingStoryId(story.story_id || story.id)
    setSelectedDate(scheduledFor ? new Date(scheduledFor) : new Date())
    setFeedback(null)
  }

  const cancelEditing = () => {
    setEditingStoryId(null)
    setSelectedDate(null)
    setFeedback(null)
  }

  const isBusy = submitting || returningStoryId !== null || regeneratingStoryId !== null

  const handleReschedule = async (storyId, forceCascade = false) => {
    if (!selectedDate || !storyId) {
      setFeedback({ type: 'error', message: t('errors:schedule.invalidTime') })
      return
    }

    if (!instagramAccountId) {
      setFeedback({
        type: 'error',
        message: t('errors:schedule.noAccount'),
      })
      return
    }

    // If in draft mode and callback provided, call it instead of direct save
    if (draftMode && onDraftChange) {
      const story = stories.find(s => (s.story_id || s.id) === storyId)
      if (story) {
        onDraftChange({
          story_id: storyId,
          old_time: new Date(resolveScheduledAt(story)),
          new_time: selectedDate,
          instagram_account_id: instagramAccountId,
          caption_text: story?.caption_preview || story?.caption_preview_text || story?.caption_text || t('upcomingSchedule.caption.missing'),
          media_type: story?.media_type || 'image'
        })
        setFeedback({ type: 'success', message: t('upcomingSchedule.success.draftAdded') })
        setEditingStoryId(null)
        setSelectedDate(null)
      }
      return
    }

    const postIntervalHours = brandSettings?.post_interval_hours || 24

    // ✅ Check if we need cascading
    const validation = validateWithCascadeOption(
      selectedDate,
      storyId,
      stories,
      postIntervalHours
    )

    // If valid, proceed normally
    if (validation.isValid) {
      try {
        setSubmitting(true)
        setFeedback(null)

        await api.rescheduleStory(storyId, {
          scheduled_for: selectedDate.toISOString(),
          instagram_account_id: instagramAccountId,
        })

        setFeedback({ type: 'success', message: t('upcomingSchedule.success.rescheduled') })
        setEditingStoryId(null)
        setSelectedDate(null)
        if (typeof onRefresh === 'function') {
          onRefresh()
        }
      } catch (err) {
        console.error('Failed to reschedule story', err)
        setFeedback({
          type: 'error',
          message: err.message || t('errors:schedule.rescheduleFailed'),
        })
      } finally {
        setSubmitting(false)
      }
      return
    }

    // If can cascade and user hasn't confirmed yet, show confirmation
    if (validation.canCascade && !forceCascade) {
      setCascadeInfo(validation.cascadeInfo)
      setPendingStoryId(storyId)
      setShowCascadeConfirm(true)
      return
    }

    // If forceCascade=true, perform bulk reschedule
    if (validation.canCascade && forceCascade) {
      try {
        setSubmitting(true)
        setFeedback(null)

        const reschedules = [
          {
            story_id: storyId,
            scheduled_for: selectedDate.toISOString()
          },
          ...validation.cascadeInfo.newSchedules.map(schedule => ({
            story_id: schedule.storyId,
            scheduled_for: schedule.newDate.toISOString()
          }))
        ]

        await api.bulkRescheduleStories(instagramAccountId, reschedules)

        const movedCount = validation.cascadeInfo.newSchedules.length
        setFeedback({
          type: 'success',
          message: t('upcomingSchedule.success.cascadeRescheduled', { count: movedCount })
        })
        setEditingStoryId(null)
        setSelectedDate(null)
        setShowCascadeConfirm(false)
        setCascadeInfo(null)
        setPendingStoryId(null)

        if (typeof onRefresh === 'function') {
          onRefresh()
        }
      } catch (err) {
        console.error('Failed to bulk reschedule stories', err)
        setFeedback({
          type: 'error',
          message: err.message || t('errors:schedule.rescheduleFailed'),
        })
      } finally {
        setSubmitting(false)
      }
      return
    }

    // Can't cascade (conflict with story before)
    const errorMessage = validation.reasonKey
      ? t(validation.reasonKey, validation.reasonParams)
      : validation.reason || t('errors:schedule.rescheduleFailed')
    setFeedback({ type: 'error', message: errorMessage })
  }

  const handleReturnToApproval = async (storyId) => {
    if (!storyId) {
      setFeedback({
        type: 'error',
        message: t('errors:schedule.noStory'),
      })
      return
    }

    if (!instagramAccountId) {
      setFeedback({
        type: 'error',
        message: t('errors:schedule.noAccount'),
      })
      return
    }

    try {
      setReturningStoryId(storyId)
      setFeedback(null)

      await api.returnStoryToApproval(storyId, {
        instagram_account_id: instagramAccountId,
      })

      setFeedback({ type: 'success', message: t('upcomingSchedule.success.returnedToApproval') })
      setEditingStoryId(null)
      setSelectedDate(null)
      if (typeof onRefresh === 'function') {
        onRefresh()
      }
    } catch (err) {
      console.error('Failed to return story to approval', err)
      setFeedback({
        type: 'error',
        message: err.message || t('errors:schedule.moveToApprovalFailed'),
      })
    } finally {
      setReturningStoryId(null)
    }
  }

  // Phase 3 Integration - Regenerate Caption with Quota Check
  const handleRegenerateCaption = async (storyId) => {
    if (!storyId) {
      setFeedback({
        type: 'error',
        message: t('errors:schedule.noStory'),
      })
      return
    }

    if (!instagramAccountId) {
      setFeedback({
        type: 'error',
        message: t('errors:schedule.noAccount'),
      })
      return
    }

    // Check quota before proceeding
    if (regenQuota.isExceeded) {
      setFeedback({
        type: 'error',
        message: t('errors:subscription.quotaExceeded'),
      })
      return
    }

    try {
      setRegeneratingStoryId(storyId)
      setFeedback(null)

      // Note: This assumes the API endpoint exists. If not, this will need to be adjusted.
      await api.regenerateScheduledStoryCaption(storyId, {
        instagram_account_id: instagramAccountId,
      })

      setFeedback({ type: 'success', message: t('upcomingSchedule.success.regenerationStarted') })
      if (typeof onRefresh === 'function') {
        setTimeout(onRefresh, 2000) // Refresh after a delay to allow processing
      }
    } catch (err) {
      console.error('Failed to regenerate caption', err)
      setFeedback({
        type: 'error',
        message: err.message || t('errors:approval.regenerationFailed', { error: t('errors:common.unknown') }),
      })
    } finally {
      setRegeneratingStoryId(null)
    }
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('upcomingSchedule.title')}</h2>
          <p className="text-sm text-gray-600">
            {nextStory
              ? t('upcomingSchedule.subtitle', { time: formatDistanceToNow(new Date(resolveScheduledAt(nextStory)), { addSuffix: true, locale: getDateLocale(i18n.language) }) })
              : t('upcomingSchedule.subtitleEmpty')}
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="text-sm font-medium text-primary-600 hover:text-primary-500"
          disabled={loading || isBusy}
        >
          {t('upcomingSchedule.button.refresh')}
        </button>
      </div>

      {feedback && (
        <div
          className={`mb-4 rounded-lg px-4 py-3 text-sm ${
            feedback.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {feedback.message}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="text-sm font-medium">{t('upcomingSchedule.error.loadFailed')}</p>
          <p className="text-xs mt-1">{error}</p>
        </div>
      ) : sortedStories.length === 0 ? (
        <div className="text-sm text-gray-600">
          {t('upcomingSchedule.empty.message')}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {sortedStories.map((story) => {
            const storyId = story.story_id || story.id
            const scheduledFor = resolveScheduledAt(story)
            const scheduledDate = scheduledFor ? new Date(scheduledFor) : null
            const captionPreview = story?.caption_preview || story?.caption_preview_text || story?.caption_text
            const isEditing = editingStoryId === storyId

            return (
              <div
                key={storyId}
                className="rounded-lg border border-gray-200 overflow-hidden bg-white hover:shadow-md transition-shadow"
              >
                {/* Image Thumbnail */}
                <div className="relative aspect-[16/9] bg-gray-100">
                  <img
                    src={resolveImageUrl(story)}
                    alt={captionPreview || 'Story preview'}
                    className="w-full h-full object-cover"
                    onError={applyFallbackIfNeeded}
                  />

                  {/* Status Badge Overlay */}
                  <div className="absolute top-2 right-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(story.status || 'scheduled')}`}>
                      {story.status || 'scheduled'}
                    </span>
                  </div>

                  {/* Video Indicator */}
                  {story.media_type === 'video' && (
                    <div className="absolute bottom-2 left-2 bg-black bg-opacity-60 rounded-full p-1.5">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
                      </svg>
                    </div>
                  )}
                </div>

                {/* Card Content */}
                <div className="p-3 space-y-2">
                  {/* Time Information */}
                  <div className="space-y-0.5">
                    <p className="text-sm font-semibold text-primary-600">
                      {scheduledDate && formatDistanceToNow(scheduledDate, { addSuffix: true, locale: getDateLocale(i18n.language) })}
                    </p>
                    <p className="text-xs text-gray-500">
                      {scheduledDate ? format(scheduledDate, 'PPp', { locale: getDateLocale(i18n.language) }) : t('upcomingSchedule.status.notScheduled')}
                    </p>
                  </div>

                  {/* Caption Preview */}
                  {captionPreview && (
                    <p className="text-xs text-gray-600 line-clamp-2">
                      {captionPreview}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="pt-2 border-t border-gray-100">
                    {!isEditing && (
                      <>
                        <button
                          type="button"
                          onClick={() => setExpandedActions(expandedActions === storyId ? null : storyId)}
                          className="w-full flex items-center justify-between text-xs text-gray-600 hover:text-gray-900 font-medium"
                          disabled={isBusy}
                        >
                          <span>{t('upcomingSchedule.button.actions') || 'Actions'}</span>
                          <svg
                            className={`w-4 h-4 transition-transform ${expandedActions === storyId ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {expandedActions === storyId && (
                          <div className="mt-2 space-y-1">
                            <button
                              type="button"
                              onClick={() => beginEditing(story)}
                              className="btn-secondary w-full text-xs py-1.5"
                              disabled={isBusy}
                            >
                              {t('upcomingSchedule.button.reschedule')}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRegenerateCaption(storyId)}
                              className="btn-secondary w-full text-xs py-1.5"
                              disabled={regenQuota.isExceeded || isBusy || regenQuota.loading}
                              title={regenQuota.isExceeded ? t('errors:subscription.quotaExceeded') : t('upcomingSchedule.button.regenerateCaption')}
                            >
                              {regeneratingStoryId === storyId ? t('upcomingSchedule.button.regenerating') : t('upcomingSchedule.button.regenerateCaption')}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleReturnToApproval(storyId)}
                              className="btn-danger w-full text-xs py-1.5"
                              disabled={isBusy}
                            >
                              {returningStoryId === storyId ? t('upcomingSchedule.button.returning') : t('upcomingSchedule.button.backToApproval')}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Reschedule Modal */}
      {editingStoryId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('upcomingSchedule.edit.title') || 'Reschedule Story'}
              </h3>
              <button
                onClick={cancelEditing}
                className="text-gray-400 hover:text-gray-500"
                disabled={submitting}
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Post Interval Info */}
              {brandSettings?.post_interval_hours && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-blue-900">
                    {t('upcomingSchedule.edit.intervalInfo', { hours: brandSettings.post_interval_hours })}
                  </p>
                </div>
              )}

              {/* Suggested Time Slots */}
              {(() => {
                const postIntervalHours = brandSettings?.post_interval_hours || 24
                const quietHours = brandSettings?.posting_schedule?.quiet_hours || []
                const timezone = brandSettings?.timezone || 'UTC'
                const suggestions = getSuggestedTimeSlots(
                  stories.filter(s => (s.story_id || s.id) !== editingStoryId),
                  postIntervalHours,
                  3,
                  quietHours,
                  timezone
                )

                return suggestions.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      {t('upcomingSchedule.edit.suggestedSlots') || 'Suggested times'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {suggestions.map((slot, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setSelectedDate(slot.date)}
                          className={`px-3 py-2 text-sm font-medium rounded transition-colors ${
                            selectedDate && selectedDate.getTime() === slot.date.getTime()
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          disabled={submitting}
                        >
                          {t(slot.labelKey, slot.labelParams)} - {format(slot.date, 'HH:mm')}
                        </button>
                      ))}
                    </div>
                  </div>
                )
              })()}

              {/* Date Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('upcomingSchedule.edit.selectTime') || 'Select date and time'}
                </label>
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={1}
                  dateFormat="dd.MM.yyyy HH:mm"
                  className="input w-full"
                  minDate={new Date()}
                  filterTime={(time) => {
                    const postIntervalHours = brandSettings?.post_interval_hours || 24
                    const quietHours = brandSettings?.posting_schedule?.quiet_hours || []
                    const timezone = brandSettings?.timezone || 'UTC'
                    return filterAvailableTime(
                      time,
                      stories,
                      postIntervalHours,
                      editingStoryId,
                      quietHours,
                      timezone
                    )
                  }}
                  placeholderText={t('upcomingSchedule.edit.placeholder')}
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 flex justify-end space-x-2 border-t border-gray-200">
              <button
                onClick={cancelEditing}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('upcomingSchedule.edit.cancel')}
              </button>
              <button
                onClick={() => handleReschedule(editingStoryId)}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {submitting ? t('upcomingSchedule.edit.saving') : t('upcomingSchedule.edit.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cascade Confirmation Modal */}
      {showCascadeConfirm && cascadeInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-yellow-50">
              <div className="flex items-center space-x-2">
                <svg className="h-5 w-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h3 className="text-base font-semibold text-gray-900">
                  {t('upcomingSchedule.cascade.title')}
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowCascadeConfirm(false)
                  setCascadeInfo(null)
                  setPendingStoryId(null)
                }}
                className="text-gray-400 hover:text-gray-500"
                disabled={submitting}
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4">
              <p className="text-sm text-gray-700 mb-3">
                {t('upcomingSchedule.cascade.description', { count: cascadeInfo.newSchedules.length, hours: brandSettings?.post_interval_hours })}
              </p>

              <div className="bg-gray-50 rounded p-3 max-h-48 overflow-y-auto space-y-2">
                {cascadeInfo.newSchedules.map((schedule, idx) => (
                  <div key={schedule.storyId} className="text-xs flex items-center justify-between bg-white rounded p-2">
                    <span className="font-medium">{t('upcomingSchedule.cascade.storyLabel', { number: idx + 1 })}</span>
                    <div className="flex items-center space-x-1 text-gray-600">
                      <span>{format(schedule.oldDate, 'dd.MM HH:mm')}</span>
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                      <span className="text-primary-600 font-medium">{format(schedule.newDate, 'dd.MM HH:mm')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 flex justify-end space-x-2 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowCascadeConfirm(false)
                  setCascadeInfo(null)
                  setPendingStoryId(null)
                }}
                disabled={submitting}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('upcomingSchedule.cascade.cancelButton')}
              </button>
              <button
                onClick={() => handleReschedule(pendingStoryId, true)}
                disabled={submitting}
                className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {submitting ? t('upcomingSchedule.cascade.confirmButtonSubmitting') : t('upcomingSchedule.cascade.confirmButton', { count: cascadeInfo.newSchedules.length })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UpcomingSchedule
